// F9 (fase 2, corte 8, #0747) — «Programar entrega» / «Cambiar entrega».
// El jefe (en sus proyectos) o el gerente fija el día y la HORA DE INICIO
// de la entrega de una unidad (PUT /assets/:id/entrega). Si la unidad
// tiene un propietario con cuenta, recibe un correo y un aviso en la app;
// si no, la fecha queda guardada y no se avisa a nadie (separar invitar de
// activar es de la fase 3).
//
// Misma estructura que ActivateUnitDrawer: portal a document.body (el
// disparador vive en un <td>), `useDialogFocus`, y la Idempotency-Key se
// crea UNA vez por apertura y se repite en cada reintento.
//
// El día y la hora son de pared de Santiago, sin zona: el servidor los
// convierte una sola vez. El DS no tiene un control de hora: `<input
// type="time" step="1800">` con la clase `.input` del formulario, como la
// visita (VisitaFields).

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  entregaLabel,
  type CarteraDashboardAssetItem,
  type UnitDeliveryResponse,
} from "@bitacorapro/api-types";
import {
  addCalendarDays,
  deliveryInstantIso,
  isDeliveryTime,
  santiagoDate,
  UNIT_DELIVERY_MAX_DAYS_AHEAD,
  UNIT_DELIVERY_TIME_STEP_MINUTES,
} from "@bitacorapro/core-engine";
import { ApiError } from "../lib/api/error";
import { useSetUnitDelivery } from "../lib/api/queries";
import { focusHeading, useDialogFocus } from "../lib/useDialogFocus";
import { Icon } from "./icons";

export type EntregaDraft = { date: string; time: string };

/** La ventana del selector de día, en Santiago. */
export function entregaWindow(now: Date = new Date()): { min: string; max: string } {
  const min = santiagoDate(now);
  return { min, max: addCalendarDays(min, UNIT_DELIVERY_MAX_DAYS_AHEAD) };
}

/** Un `<input type="time">` puede entregar «10:00:00»: el wire es «10:00». */
function hhmm(t: string): string {
  return /^\d{2}:\d{2}:00$/.test(t) ? t.slice(0, 5) : t;
}

/** Qué le falta al borrador para poder guardarse; null = listo. */
export function faltaDeEntrega(v: EntregaDraft, now: Date = new Date()): string | null {
  if (v.date === "") return "Elige el día de la entrega.";
  if (v.time === "") return "Elige la hora de inicio.";
  const hora = hhmm(v.time);
  if (!isDeliveryTime(hora)) {
    return "La hora va en bloques de 30 minutos (por ejemplo, 10:00 o 10:30).";
  }
  const { min, max } = entregaWindow(now);
  if (v.date < min || v.date > max) {
    return `El día tiene que ser hoy o uno de los próximos ${UNIT_DELIVERY_MAX_DAYS_AHEAD} días.`;
  }
  if (Date.parse(deliveryInstantIso(v.date, hora)) <= now.getTime()) {
    return "Esa hora ya pasó. Elige una hora posterior.";
  }
  return null;
}

/** El error del servidor en español, por código. */
export function entregaErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "DELIVERY_DATE_OUT_OF_RANGE":
        return `La entrega tiene que ser después de este momento y dentro de los próximos ${UNIT_DELIVERY_MAX_DAYS_AHEAD} días.`;
      case "DELIVERY_ALREADY_HAPPENED":
        return "Esta unidad ya fue entregada: su entrega no se puede reprogramar.";
      case "DELIVERY_ACTOR_NOT_ALLOWED":
        return "Tu rol no puede programar entregas.";
      case "DELIVERY_NOT_ENABLED":
        return "Esta organización no programa entregas de unidades.";
      case "ASSET_OUT_OF_SCOPE":
        return "No pudimos abrir esta unidad. Es posible que ya no esté disponible o que no tengas acceso a ella.";
    }
  }
  return "No pudimos guardar la fecha. Puede ser un problema de conexión — vuelve a intentar.";
}

/** Qué se le dijo al propietario, en una frase para el jefe. */
export function resultadoEntrega(r: UnitDeliveryResponse): string {
  if (!r.changed) return "Era la misma fecha y hora: no se volvió a avisar al propietario.";
  if (!r.ownerHasAccount) {
    return "La fecha quedó guardada. La unidad todavía no tiene un propietario con cuenta, así que no se le avisó.";
  }
  switch (r.ownerNotice) {
    case "sent":
      return "Le avisamos al propietario por correo y en la app.";
    case "suppressed_by_preference":
      return "El propietario lo verá en la app. Eligió no recibir este aviso por correo.";
    default:
      return "La fecha quedó guardada y el propietario la verá en la app, pero no pudimos enviarle el correo.";
  }
}

export function EntregaDrawer({
  asset,
  assetLabel,
  onClose,
}: {
  asset: CarteraDashboardAssetItem;
  /** La palabra de la receta para una unidad («Unidad»). */
  assetLabel: string;
  onClose: () => void;
}) {
  const titleId = useId();
  const dateId = useId();
  const timeId = useId();
  const hintId = useId();
  const drawerRef = useRef<HTMLElement>(null);
  const successRef = useRef<HTMLHeadingElement>(null);
  const idemKeyRef = useRef<string>(crypto.randomUUID());

  const vigente = asset.entrega ?? null;
  // La hora vigente se trae sólo si es una hora que se puede guardar: la
  // planilla admite cualquier minuto («10:15») y el drawer no puede
  // cargar un borrador que él mismo rechaza.
  const [draft, setDraft] = useState<EntregaDraft>({
    date: vigente?.date ?? "",
    time: vigente?.time && isDeliveryTime(vigente.time) ? vigente.time : "",
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const mutation = useSetUnitDelivery();

  useDialogFocus(true, drawerRef);
  useEffect(() => {
    if (mutation.isSuccess) focusHeading(successRef);
  }, [mutation.isSuccess]);

  const falta = faltaDeEntrega(draft);
  const { min, max } = entregaWindow();
  const step = UNIT_DELIVERY_TIME_STEP_MINUTES * 60;
  const unidad = asset.unit || asset.address || "(sin unidad)";
  const heading = vigente ? "Cambiar entrega" : "Programar entrega";

  async function onSubmit() {
    if (falta !== null || mutation.isPending) return;
    setSubmitError(null);
    try {
      await mutation.mutateAsync({
        assetId: asset.id,
        body: { date: draft.date, time: hhmm(draft.time) },
        idempotencyKey: idemKeyRef.current,
      });
    } catch (err) {
      setSubmitError(entregaErrorMessage(err));
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") onClose();
  }

  return createPortal(
    <>
      <div className="drawer-backdrop" onClick={onClose} role="presentation" />
      <aside
        ref={drawerRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="drawer__head">
          <h2 id={titleId} className="drawer__title">
            {heading}
          </h2>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="Cerrar">
            <Icon.Close />
          </button>
        </div>

        <div className="drawer__body">
          {mutation.isSuccess && mutation.data ? (
            <>
              <h3 ref={successRef} tabIndex={-1} className="drawer__obs-title">
                Entrega: {entregaLabel(mutation.data.entrega)}
              </h3>
              <p className="cell-sub drawer__obs-meta" role="status">
                {resultadoEntrega(mutation.data)}
              </p>
            </>
          ) : (
            <>
              <p className="cell-sub drawer__obs-meta">
                {assetLabel}: {unidad}
                {vigente ? ` · Entrega actual: ${entregaLabel(vigente, new Date())}` : ""}
              </p>
              {submitError && (
                <p className="auth-error" role="alert">
                  {submitError}
                </p>
              )}
              <div className="grid-2">
                <div className="field">
                  <label className="field__label" htmlFor={dateId}>
                    Día de la entrega
                  </label>
                  <input
                    id={dateId}
                    type="date"
                    className="input"
                    min={min}
                    max={max}
                    value={draft.date}
                    disabled={mutation.isPending}
                    aria-describedby={hintId}
                    onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label className="field__label" htmlFor={timeId}>
                    Hora de inicio
                  </label>
                  <input
                    id={timeId}
                    type="time"
                    step={step}
                    className="input"
                    value={draft.time}
                    disabled={mutation.isPending}
                    aria-describedby={hintId}
                    onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                  />
                </div>
              </div>
              <span className="field__hint" id={hintId}>
                {asset.ownerHasAccount
                  ? "El propietario recibirá un correo y un aviso en la app con el día y la hora."
                  : "La unidad todavía no tiene un propietario con cuenta: la fecha queda guardada, pero no se le avisará."}
              </span>
              {falta !== null && (draft.date !== "" || draft.time !== "") && (
                <p className="field__hint" role="status">
                  {falta}
                </p>
              )}
            </>
          )}
        </div>

        <div className="drawer__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {mutation.isSuccess ? "Cerrar" : "Cancelar"}
          </button>
          {!mutation.isSuccess && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void onSubmit()}
              disabled={falta !== null || mutation.isPending}
            >
              {mutation.isPending ? "Guardando…" : "Guardar entrega"}
            </button>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
}
